import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import VendorHeader from "./../../component/VendorHeader";
import VendorFooter from "./../../component/VendorFooter";
import { API_BASE_URL } from "../../utils/app_url";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../../component/Sidebar";
import JoditEditor from "jodit-react";

const deleteIcon = require("./../../assets/img/delete-icon.svg").default;

const ALLOWED_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

const AddBlog = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { selectedLanguage, langJson } = useLanguage();
  const featuredImageRef = useRef();
  const additionalImagesRef = useRef();
  const editorRef = useRef(null);

  const editorConfig = {
    height: 400,
    minHeight: 400,
    buttons: [
      "source",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "ul",
      "ol",
      "|",
      "left",
      "center",
      "right",
      "justify",
      "|",
      "link",
      "image",
    ],
    uploader: {
      insertImageAsBase64URI: true,
    },
    removeButtons: ["brush", "file"],
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    toolbarAdaptive: true,
  };

  const [addFormData, setAddFormData] = useState({
    title: "",
    description: "",
    excerpt: "",
    author: "",
    status: false,
    is_featured: false,
    published_at: "",
  });
  const [featuredImage, setFeaturedImage] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [signupStatus, setSignupStatus] = useState("");
  const [addBtnDis, setAddBtnDis] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setAddFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onFeaturedImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (ALLOWED_EXTENSIONS.includes(ext)) {
      setFeaturedImage(file);
      setSignupStatus("");
    } else {
      setFeaturedImage(null);
      setSignupStatus(langJson?.invalid_file_format_Please_upload_a_valid__image_file ?? "Invalid file format. Please upload a valid image file.");
    }
  };

  const onAdditionalImagesChange = (e) => {
    const files = Array.from(e.target.files);
    const valid = files.filter(f => ALLOWED_EXTENSIONS.includes(f.name.split(".").pop().toLowerCase()));
    setAdditionalImages(prev => [...prev, ...valid]);
  };

  const removeAdditionalImage = (index) => {
    setAdditionalImages(prev => prev.filter((_, i) => i !== index));
  };

  const onPostArticle = async () => {
    if (!addFormData.title) {
      setSignupStatus(langJson?.titleisrequired ?? "Title is required");
    } else if (!addFormData.description) {
      setSignupStatus(langJson?.descriptionisrequired ?? "Description is required");
    } else {
      setAddBtnDis(true);
      setSignupStatus("");

      const fd = new FormData();
      fd.append("title", addFormData.title);
      fd.append("description", addFormData.description);
      fd.append("excerpt", addFormData.excerpt);
      fd.append("author", addFormData.author);
      fd.append("status", addFormData.status ? 1 : 0);
      fd.append("is_featured", addFormData.is_featured ? 1 : 0);
      if (addFormData.published_at) {
        fd.append("published_at", addFormData.published_at.replace("T", " ") + ":00");
      }
      fd.append("lang", selectedLanguage);
      if (featuredImage) {
        fd.append("featured_image", featuredImage);
      }
      additionalImages.forEach(img => fd.append("images[]", img));

      try {
        const response = await fetch(`${API_BASE_URL}stores/admin/blogs`, {
          method: "POST",
          body: fd,
          headers: { Authorization: `Bearer ${user.authorisation.token}` },
        });
        const responseData = await response.json();
        if (responseData.res === true) {
          navigate("/admin/blogs");
        } else {
          setSignupStatus(responseData.msg ?? "Error creating blog");
          setAddBtnDis(false);
        }
      } catch (error) {
        console.error("Error creating blog:", error);
        setSignupStatus("Error creating blog");
        setAddBtnDis(false);
      }
    }
  };

  const getTodayDateTime = () => {
    return new Date().toISOString().slice(0, 16);
  };

  return (
    <div>
      <div className="dashHeader">
        <VendorHeader />
      </div>
      <div className="adminDash">
        <Sidebar />
        <div className="rightSide">
          <h2>{langJson?.add ?? "Add"} {langJson?.blog ?? "Blog"}</h2>
          <ul className="addAddress">
            <li className="fullWidth">
              <label>{langJson?.title}</label>
              <input type="text" name="title" onChange={handleChange} value={addFormData.title} />
            </li>
            <li className="fullWidth">
              <label>{langJson?.author ?? "Author"}</label>
              <input type="text" name="author" onChange={handleChange} value={addFormData.author} />
            </li>
            <li className="fullWidth">
              <label>{langJson?.excerpt ?? "Excerpt"}</label>
              <textarea name="excerpt" value={addFormData.excerpt} onChange={handleChange}></textarea>
            </li>
            <li className="fullWidth jodit-wrapper">
              <label>{langJson?.description ?? "Description"}</label>
              <JoditEditor
                ref={editorRef}
                value={addFormData.description}
                config={editorConfig}
                onChange={(value) => setAddFormData(prev => ({ ...prev, description: value }))}
              />
            </li>
            <li>
              <label>{langJson?.publishedat ?? "Publish Date"}</label>
              <input
                type="datetime-local"
                name="published_at"
                min={getTodayDateTime()}
                onChange={handleChange}
                value={addFormData.published_at || ""}
              />
            </li>
            <li>
              <label>
                <input type="checkbox" name="status" checked={addFormData.status} onChange={handleChange} />
                {" "}{langJson?.published ?? "Published"}
              </label>
            </li>
            <li>
              <label>
                <input type="checkbox" name="is_featured" checked={addFormData.is_featured} onChange={handleChange} />
                {" "}{langJson?.featured ?? "Featured"}
              </label>
            </li>

            {/* Featured image */}
            <li className="fullWidth">
              <label>{langJson?.uploadcoverimage ?? "Upload Featured Image"}:</label>
              {!featuredImage ? (
                <>
                  <input type="file" ref={featuredImageRef} hidden accept="image/jpg,image/jpeg,image/png,image/webp" onChange={onFeaturedImageChange} />
                  <button type="button" onClick={() => featuredImageRef.current.click()}>
                    {langJson?.choosecoverimage ?? "Choose Featured Image"}
                  </button>
                </>
              ) : (
                <div className="coverPreviewWrap">
                  <img className="coverPreviewImg" src={URL.createObjectURL(featuredImage)} alt="" />
                  <img className="coverPreviewDelete" src={deleteIcon} alt="" onClick={() => setFeaturedImage(null)} />
                </div>
              )}
            </li>

            {/* Additional images */}
            <li className="fullWidth">
              <label>{langJson?.additionalimages ?? "Additional Images"}:</label>
              <input type="file" ref={additionalImagesRef} hidden accept="image/jpg,image/jpeg,image/png,image/webp" multiple onChange={onAdditionalImagesChange} />
              <button type="button" onClick={() => additionalImagesRef.current.click()}>
                {langJson?.chooseimages ?? "Choose Images"}
              </button>
              {additionalImages.length > 0 && (
                <div className="additionalImagesWrap">
                  {additionalImages.map((img, i) => (
                    <div key={i} className="coverPreviewWrap">
                      <img className="coverPreviewImg" src={URL.createObjectURL(img)} alt="" />
                      <img className="coverPreviewDelete" src={deleteIcon} alt="" onClick={() => removeAdditionalImage(i)} />
                    </div>
                  ))}
                </div>
              )}
            </li>

            <li className="fullWidth saveBtn">
              <button type="button" disabled={addBtnDis} onClick={onPostArticle}>
                {addBtnDis ? langJson?.pleasewait : langJson?.savechanges}
              </button>
              <Link className="backLink" to="/admin/blogs">{langJson?.back ?? "Back"}</Link>
            </li>
            <span className="lgnStatus">{signupStatus}</span>
          </ul>
        </div>
      </div>
      <VendorFooter />
    </div>
  );
};

export default AddBlog;
